import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Leaf, Mail, Lock, User, UserPlus, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";

export default function Register() {
    const { t } = useT();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { register, error, setError } = useAuth();
    const nav = useNavigate();

    async function onSubmit(e) {
        e.preventDefault();
        if (password.length < 6) {
            setError(t("register.password_too_short"));
            return;
        }
        setSubmitting(true);
        const ok = await register(email.trim(), password, name.trim());
        setSubmitting(false);
        if (ok) {
            toast.success(t("register.toast_success"));
            nav("/", { replace: true });
        }
    }

    return (
        <div className="min-h-screen bg-bone grid place-items-center px-4 py-10">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center justify-center gap-3 mb-8">
                    <div className="w-14 h-14 rounded-2xl bg-forest text-white grid place-items-center shadow-md">
                        <Leaf size={28} />
                    </div>
                    <div>
                        <div className="font-display text-2xl text-forest-dark leading-tight">HACCP Manager</div>
                        <div className="text-xs text-stone-500 -mt-0.5">Inizia in 30 secondi</div>
                    </div>
                </Link>

                <div className="bg-white rounded-2xl shadow-sm border border-soft p-7" data-testid="register-card">
                    <div className="flex items-center gap-2 mb-5 px-3 py-2 bg-accent-warm/10 text-terracotta rounded-lg">
                        <Sparkles size={16} />
                        <span className="text-sm font-medium">7 giorni gratis · Nessuna carta richiesta</span>
                    </div>
                    <h1 className="font-display text-2xl text-forest-dark mb-1">Crea il tuo account</h1>
                    <p className="text-sm text-stone-500 mb-6">Inizia a gestire HACCP, spese e scadenze del tuo negozio.</p>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <label className="block">
                            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Nome</span>
                            <div className="mt-1 relative">
                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setError(null);
                                    }}
                                    className="input pl-10"
                                    placeholder="Mario Rossi"
                                    data-testid="register-name"
                                />
                            </div>
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Email</span>
                            <div className="mt-1 relative">
                                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                <input
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setError(null);
                                    }}
                                    className="input pl-10"
                                    placeholder="email@esempio.it"
                                    data-testid="register-email"
                                />
                            </div>
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">{t("register.password_hint")}</span>
                            <div className="mt-1">
                                <PasswordInput
                                    required
                                    autoComplete="new-password"
                                    minLength={6}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setError(null);
                                    }}
                                    className="input"
                                    placeholder="••••••••"
                                    data-testid="register-password"
                                    iconLeft={<Lock size={16} />}
                                />
                            </div>
                        </label>

                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="register-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full inline-flex items-center justify-center gap-2"
                            data-testid="register-submit"
                        >
                            <UserPlus size={16} />
                            {submitting ? "Creazione account..." : "Inizia prova gratuita"}
                        </button>
                    </form>

                    <p className="text-xs text-stone-500 text-center mt-5">
                        Hai già un account?{" "}
                        <Link to="/login" className="text-forest font-medium hover:underline" data-testid="register-login-link">
                            Accedi
                        </Link>
                    </p>
                </div>

                <p className="text-center text-xs text-stone-500 mt-6">
                    Iscrivendoti accetti le condizioni d'uso · Disdici in qualsiasi momento
                </p>
            </div>
        </div>
    );
}
